import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from './Footer';
import './Insights.css';
import { WalkIcon, SleepIcon, LotusIcon, SLEEP_COLOR } from './activityShared';
import { TrendingUpIcon } from './Icons';
import { API, authFetch } from '../api';
import {
  hasValidInsightsCache,
  loadStoredInsights,
  loadStoredModelStatus,
  storeInsights,
} from '../utils/insightsStore';
import {
  DASHBOARD_INSIGHTS_PREVIEW_LIMIT,
  DASHBOARD_TIPS_PREVIEW_LIMIT,
  INSIGHTS_PAGE_INITIAL_LIMIT,
  FALLBACK_HEALTH_INSIGHTS,
  FALLBACK_HEALTH_TIPS,
  STATIC_RECOVERY_STATUS,
} from './healthInsights';
import ModelStatusBanner from './ModelStatusBanner';
import { useModelStatus } from '../hooks/useModelStatus';
import { useOverview } from '../hooks/useOverview';

const CHART_DATA = [
  { day: 'Mon', val: 64, active: false },
  { day: 'Tue', val: 90, active: false },
  { day: 'Wed', val: 73, active: false },
  { day: 'Thu', val: 104, active: false },
  { day: 'Fri', val: 85, active: false },
  { day: 'Sat', val: 92, active: false },
  { day: 'Today', val: 113, active: true },
];

const TODAY_VALUE = 58;
const BASELINE_VALUE = 52;

function PulseIcon() {
  return (
    <svg width="24" height="18" viewBox="0 0 24 18" fill="none">
      <path
        d="M1 9H5L8 2L12 16L16 6L18 9H23"
        stroke="#434F4D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HeroArrow() {
  return (
    <svg width="150" height="100" viewBox="0 0 160 100" fill="none">
      <path
        d="M100.072 7.5H141.214V48.6429"
        stroke="#A7F59E"
        strokeWidth="15"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M141.214 7.5L83.1 65.6143C82.1387 66.5566 80.8462 67.0844 79.5 67.0844C78.1538 67.0844 76.8614 66.5566 75.9 65.6143L52.2429 41.9571C51.2815 41.0148 49.989 40.487 48.6429 40.487C47.2967 40.487 46.0042 41.0148 45.0429 41.9571L7.5 79.5"
        stroke="#A7F59E"
        strokeWidth="15"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Five filled inline SVGs for the five insight categories. Same
// viewBox + single-path convention as the activity CategoryIcon set,
// so the icon family is consistent across the app. The "state of
// health" glyph is a pulse / EKG line — explicitly NOT a heart.
function InsightCategoryIcon({ category, color, size = 22 }) {
  const c = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none' };
  const stroke = { stroke: color, strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' };

  switch (category) {
    case 'state-of-health':
      // EKG / pulse line. Stroked rather than filled so the heart-rate
      // shape reads cleanly at small sizes.
      return (
        <svg {...c}>
          <path d="M2 12h4l2-6 4 12 4-9 2 3h4" {...stroke} fill="none" />
        </svg>
      );
    case 'processing-of-stress':
      // Leaf — calm / nervous-system regulation.
      return (
        <svg {...c} fill={color}>
          <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z" />
        </svg>
      );
    case 'biological-age':
      // Flower — vitality / cellular health.
      return (
        <svg {...c} fill={color}>
          <path d="M12 22a4 4 0 0 1-3.4-6.06A3.99 3.99 0 0 1 6 12a3.99 3.99 0 0 1 2.6-3.94A4 4 0 0 1 12 2a4 4 0 0 1 3.4 6.06A3.99 3.99 0 0 1 18 12a3.99 3.99 0 0 1-2.6 3.94A4 4 0 0 1 12 22zm0-12a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
        </svg>
      );
    case 'performance-potential':
      // Lightning bolt — energy / readiness.
      return (
        <svg {...c} fill={color}>
          <path d="M11 21h-1l1-7H6.5c-.88 0-.33-.75-.31-.78C7.48 10.94 9.41 7.54 12.07 3h1l-1 7h4.51c.4 0 .62.19.4.66C12.97 17.55 11 21 11 21z" />
        </svg>
      );
    case 'burnout-resistance':
      // Shield with check — resilience.
      return (
        <svg {...c} fill={color}>
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 15l-4-4 1.41-1.41L10 13.17l6.59-6.59L18 8l-8 8z" />
        </svg>
      );
    default:
      return <TrendingUpIcon size={size} color={color} />;
  }
}

// Per-insight icon + colour. Replaces the older sleep/trend dichotomy
// with a real one-icon-per-category mapping.
function getInsightIcon(insight) {
  const colour = insight.iconColor || (insight.tone === 'purple' ? '#6366f1' : '#10b981');
  return { color: colour };
}

function HeroCard() {
  return (
    <div className="insights-hero-card">
      <div className="insights-hero-arrow">
        <HeroArrow />
      </div>
      <div className="insights-hero-content">
        <h1 className="insights-hero-title">{STATIC_RECOVERY_STATUS.title}</h1>
        <p className="insights-hero-subtitle">{STATIC_RECOVERY_STATUS.body}</p>
      </div>
    </div>
  );
}

// Personal Insights — 5 fixed-order categories. First two are visible
// by default; "See all 5" reveals the rest. Tapping a row opens the
// detail modal with the long `description`.
function PersonalInsightsSection({ sectionRef, onOpen, aiInsights, loading, modelStatus}) {
  const [expanded, setExpanded] = useState(false);

  // Map AI insights from backend to insight categories
  // Backend returns: [{text, status, metric}, ...]
  const getInsightsWithAiData = () => {
    // Ensure aiInsights is an array, otherwise use fallback
    if (!Array.isArray(aiInsights) || aiInsights.length < 5) return FALLBACK_HEALTH_INSIGHTS;

    // Map metric names to insight indices
    const metricToIndex = {
      'processingOfStress': 1,
      'burnoutResistance': 4,
      'performance': 3,
      'biologicalAge': 2,
      'healthState': 0,
    };

    return FALLBACK_HEALTH_INSIGHTS.map((insight, idx) => {
      // Find the AI insight that matches this category
      const aiInsight = aiInsights.find(item => metricToIndex[item.metric] === idx);
      if (aiInsight && aiInsight.text) {
        return {
          ...insight,
          body: aiInsight.text,
          preview: aiInsight.text,
          description: aiInsight.text,
          metric: aiInsight.status,
        };
      }
      return insight;
    });
  };

  const insights = getInsightsWithAiData();
  const visible = expanded
    ? insights
    : insights.slice(0, INSIGHTS_PAGE_INITIAL_LIMIT);
  const hasMore = insights.length > INSIGHTS_PAGE_INITIAL_LIMIT;

  // Render different states based on model status
  const renderContent = () => {
    if (loading) {
      return (
        <div className="insights-loading-card">
          <div className="insights-loading-spinner" aria-hidden="true" />
          <div className="insights-loading-text-wrap">
            <p className="insights-loading-title">Analyzing your health data…</p>
            <p className="insights-loading-sub">
              Pulling your latest results from the model.
            </p>
          </div>
        </div>
      );
    }

    // Models are running - show processing message
    if (modelStatus === 'RUNNING' || modelStatus === 'STARTED') {
      return (
        <div className="insights-processing">
          <div className="insights-processing-icon">🔄</div>
          <p className="insights-processing-text">
            Analyzing your measurement data...
          </p>
          <p className="insights-processing-subtext">
            This may take a few minutes. Results will appear automatically.
          </p>
        </div>
      );
    }

    // Failed to load
    if (modelStatus === 'FAILED') {
      return (
        <div className="insights-error">
          <div className="insights-error-icon">⚠️</div>
          <p className="insights-error-text">
            Failed to analyze your data.
          </p>
          <button 
            className="insights-retry-btn"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      );
    }

    // No active measurement — kompakt yatay görünüm: küçük ikon + kısa
    // başlık + tek satır ipucu. Önceki ortalanmış-büyük varyasyon kart
    // içinde aşırı dikey alan kaplıyordu.
    if (modelStatus === 'NOTHING' || modelStatus === 'NO_DATA') {
      return (
        <div
          className="insights-empty"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 14,
            padding: '18px 16px',
          }}
        >
          <div
            className="insights-empty-icon"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'rgba(16, 185, 129, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: 0,
            }}
          >
            <TrendingUpIcon size={20} color="#10b981" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <p
              className="insights-empty-text"
              style={{ margin: 0, fontSize: 14, fontWeight: 600 }}
            >
              No measurement data yet
            </p>
            <p
              className="insights-empty-subtext"
              style={{ margin: 0, fontSize: 13, opacity: 0.75 }}
            >
              Complete a 12+ hour measurement to unlock personalized insights.
            </p>
          </div>
        </div>
      );
    }

    // Show insights
    return visible.map((insight) => {
      const { color } = getInsightIcon(insight);
      return (
        <button
          key={insight.id}
          type="button"
          className="personal-insight-item personal-insight-item-button"
          onClick={() => onOpen(insight)}
        >
          <div
            className="personal-insight-icon"
            style={{ backgroundColor: `${color}15` }}
          >
            <InsightCategoryIcon category={insight.id} color={color} size={20} />
          </div>

          <div className="personal-insight-copy">
            <h4 className="personal-insight-title">{insight.title}</h4>
            <p className="personal-insight-body">{insight.preview || insight.body}</p>
          </div>

          <div className="personal-insight-metric-wrap">
            <span className="personal-insight-metric" style={{ color }}>
              {insight.metric}
            </span>
            {insight.statusWord && (
              <span className="personal-insight-status-word" style={{ color }}>
                {insight.statusWord}
              </span>
            )}
            <span className="personal-insight-metric-label">
              {insight.metricLabel}
            </span>
          </div>
        </button>
      );
    });
  };

  return (
    <div ref={sectionRef} className="card personal-insights-section insights-section-anchor">
      <div className="personal-insights-heading-row">
        <h3 className="personal-insights-heading">Personal Insights</h3>
        {modelStatus === 'COMPLETED' && hasMore && (
          <button
            type="button"
            className="insights-see-more-btn"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? 'Show less' : `See all ${insights.length}`}
          </button>
        )}
      </div>

      <div className="personal-insights-list">
        {renderContent()}
      </div>
    </div>
  );
}

function HealthTipsSection({ sectionRef, onOpen }) {
  const tips = FALLBACK_HEALTH_TIPS.slice(0, DASHBOARD_TIPS_PREVIEW_LIMIT);

  return (
    <div ref={sectionRef} className="card health-tips-section insights-section-anchor">
      <h3 className="health-tips-heading">Health Tips</h3>

      <div className="health-tips-grid">
        {tips.map((tip) => (
          <button
            key={tip.id}
            type="button"
            className="health-tip-card health-tip-card-button"
            onClick={() => onOpen(tip)}
          >
            <h4 className="health-tip-title">{tip.title}</h4>
            <p className="health-tip-body">{tip.preview || tip.body}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// Detail modal — sober styling on purpose: white card, dark backdrop,
// no gradient. Used for both insights (with the category icon) and
// tips (no icon).
function InsightDetailModal({ open, item, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !item) return null;

  // Tips don't have an `id` from the insight category set, so we only
  // render the icon block when the item is an insight with a known id.
  const isInsight = !!FALLBACK_HEALTH_INSIGHTS.find((i) => i.id === item.id);
  const color = item.iconColor || (item.tone === 'purple' ? '#6366f1' : '#10b981');

  return (
    <div className="insight-detail-overlay" onClick={onClose} role="presentation">
      <div
        className="insight-detail-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={item.title}
      >
        <button
          type="button"
          className="insight-detail-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {isInsight && (
          <div
            className="insight-detail-icon"
            style={{ backgroundColor: `${color}15` }}
          >
            <InsightCategoryIcon category={item.id} color={color} size={28} />
          </div>
        )}

        <h2 className="insight-detail-title">{item.title}</h2>
        <p className="insight-detail-body">
          {item.description || item.body || item.preview}
        </p>
      </div>
    </div>
  );
}

function HRVCard() {
  const maxVal = Math.max(...CHART_DATA.map((d) => d.val));

  return (
    <div className="card hrv-card">
      <div className="hrv-title-row">
        <PulseIcon />
        <h2 className="hrv-title">Heart Rate Variability</h2>
      </div>
      <p className="hrv-desc">Your HRV is above your baseline, indicating good recovery.</p>

      <div className="hrv-chart-wrapper">
        <span className="hrv-chart-label">Last 7 Days : {TODAY_VALUE}</span>

        <div className="hrv-chart">
          <div className="hrv-chart-dashed-line" />
          {CHART_DATA.map((item, i) => {
            const heightPct = (item.val / maxVal) * 82;
            return (
              <div key={i} className="hrv-bar-wrapper">
                <div className="hrv-bar-container">
                  {item.active && (
                    <div className="hrv-value-bubble">
                      <span className="hrv-value-text">{TODAY_VALUE}</span>
                    </div>
                  )}
                  <div
                    className={`hrv-bar ${item.active ? 'is-active' : ''}`}
                    style={{ height: `${heightPct}%` }}
                  />
                </div>
                <span className={`hrv-day-label ${item.active ? 'is-active' : ''}`}>
                  {item.day}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="hrv-baseline-row">
        <div className="hrv-baseline-line" />
        <div className="hrv-baseline-badge">Baseline: {BASELINE_VALUE} ms</div>
      </div>
    </div>
  );
}

function QuickActions() {
  const navigate = useNavigate();

  const actions = [
    { label: 'Activity', grad: ['#FFCC00', '#FFE500'], Icon: WalkIcon, category: 'exercise' },
    { label: 'Sleep', grad: ['#0084FF', '#BBF1FF'], Icon: SleepIcon, category: 'sleep' },
    { label: 'Meditation', grad: ['#7700FF', '#DFBBFF'], Icon: LotusIcon, category: 'other' },
  ];

  const handleLog = (category) => {
    navigate('/activities', {
      state: { preselectedCategory: category, openModal: true },
    });
  };

  return (
    <div className="card quick-actions-card">
      <h3 className="quick-actions-title">Quick Actions</h3>
      <div className="quick-actions-row">
        {actions.map((act, i) => {
          const IconComp = act.Icon;
          return (
            <div key={i} className="action-box">
              <span className="action-label">{act.label}</span>
              <div
                className="action-icon-circle"
                style={{
                  background: `radial-gradient(circle, ${act.grad[0]}66 0%, ${act.grad[1]}66 100%)`,
                }}
              >
                <IconComp color="#F2F2F2" />
              </div>
              <button className="action-log-btn" onClick={() => handleLog(act.category)}>
                Log
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Insights() {
  const location = useLocation();
  const insightsRef = useRef(null);
  const tipsRef = useRef(null);
  const [modalItem, setModalItem] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [modelStatus, setModelStatus] = useState(null); // 'NOTHING', 'RUNNING', 'STARTED', 'FAILED', 'COMPLETED'
  const [loading, setLoading] = useState(true);

  // Init handshake — drives the top banner (computing… / FAILED retry).
  // Hidden during NOTHING/idle so the page looks normal when there's
  // nothing to show.
  const {
    status: handshakeStatus,
    phase: modelPhase,
    refresh: refreshModel,
  } = useModelStatus();

  // Overview snapshot — held here for future wiring into the Personal
  // Insights rows. The current cards are sourced from the static
  // FALLBACK_HEALTH_INSIGHTS list; once we want to put live numbers in
  // them, read from `overview` and swap. Loaded now so the banner can
  // refresh both on retry.
  const { refresh: refreshOverview } = useOverview();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');

    const target =
      section === 'tips'
        ? tipsRef.current
        : section === 'insights'
          ? insightsRef.current
          : null;

    if (target) {
      window.requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.search]);

  // Step 1: Call /api/v1/models/init to check model status
  // Step 2: Based on status, either wait or fetch model outputs
  useEffect(() => {
    console.log('[Insights] === PAGE MOUNTED ===');

    // Check cache first - only use if status is COMPLETED (not NO_DATA or FAILED)
    if (hasValidInsightsCache()) {
      const cachedInsights = loadStoredInsights();
      const cachedStatus = loadStoredModelStatus();
      // Only use cache if it's a valid completed state with actual data
      if (cachedInsights && cachedStatus === 'COMPLETED' && cachedInsights.length > 0) {
        setAiInsights(cachedInsights);
        setModelStatus(cachedStatus);
        setLoading(false);
        console.log('[Insights] Loaded from cache:', cachedStatus, cachedInsights.length, 'items');
        return;
      }
      // Invalid cache state, clear it
      console.log('[Insights] Invalid cache state (', cachedStatus, '), clearing and fetching fresh');
    }

    console.log('[Insights] No valid cache - fetching from API');

    async function initAndFetchModels() {
      console.log('[Insights] Starting model initialization flow...');
      try {
        // Step 1: Call init endpoint
        console.log('[Insights] [1/2] POST /api/v1/models/init - Checking model status...');
        const initResponse = await authFetch(API.modelInit(), {
          method: 'POST',
        });

        console.log('[Insights] [1/2] Response status:', initResponse.status, initResponse.ok ? 'OK' : 'FAIL');

        if (!initResponse.ok) {
          console.error('[Insights] [1/2] FAILED - init endpoint returned:', initResponse.status);
          setModelStatus('FAILED');
          setLoading(false);
          return;
        }

        const initData = await initResponse.json();
        const status = initData.status;
        console.log('[Insights] [1/2] SUCCESS - Model status from backend:', status);
        console.log('[Insights] [1/2] Full init response:', initData);
        setModelStatus(status);

        // If models are running/started, we don't fetch results yet
        // User will see "models are running" message
        if (status === 'RUNNING' || status === 'STARTED') {
          console.log('[Insights] Models are', status, '- waiting for completion, skipping output fetch');
          setLoading(false);
          return;
        }

        // If FAILED, show retry message
        if (status === 'FAILED') {
          console.log('[Insights] Model init FAILED - showing error state');
          setLoading(false);
          return;
        }

        // If NOTHING, models won't run, try to fetch existing cached results
        // Step 2: Fetch model output overview
        console.log('[Insights] Model status is', status, '- proceeding to fetch outputs...');
        await fetchModelOutputs();

      } catch (error) {
        console.error('[Insights] ERROR in initAndFetchModels:', error);
        setModelStatus('FAILED');
        setLoading(false);
      }
    }
    
    async function fetchModelOutputs() {
      console.log('[Insights] [2/2] GET /api/v1/models/output-overview - Fetching model results...');
      try {
        const response = await authFetch(API.modelOverview(), {
          method: 'GET',
        });
        console.log('[Insights] [2/2] Response status:', response.status, response.ok ? 'OK' : 'FAIL');
        
        if (response.ok) {
          const data = await response.json();
          console.log('[Insights] [2/2] SUCCESS - Raw response data:', data);
          
          if (data.hasOutput !== false && data.insights && data.insights.length > 0) {
            console.log('[Insights] [2/2] Insights found:', data.insights.length, 'items');
            console.log('[Insights] [2/2] Insights data:', data.insights);
            setAiInsights(data.insights);
            setModelStatus('COMPLETED');
            // Save to cache only on successful data
            storeInsights(data.insights, 'COMPLETED');
            console.log('[Insights] [2/2] Saved to cache');
          } else {
            console.log('[Insights] [2/2] No insights data - hasOutput:', data.hasOutput, 'insights:', data.insights);
            setModelStatus('NO_DATA');
            // DO NOT cache NO_DATA state - allow re-fetch on next load
          }
        } else {
          console.error('[Insights] [2/2] FAILED - output-overview returned:', response.status);
          setModelStatus('FAILED');
        }
      } catch (error) {
        console.error('[Insights] [2/2] ERROR in fetchModelOutputs:', error);
        setModelStatus('FAILED');
      } finally {
        console.log('[Insights] [2/2] Flow complete, setting loading=false');
        setLoading(false);
      }
    }
    
    console.log('[Insights] Triggering initAndFetchModels()...');
    initAndFetchModels();
  }, []);

  return (
    <div className="insights-page-wrapper">
      <Navbar />
      <div className="insights-container">
        <ModelStatusBanner
          status={handshakeStatus}
          phase={modelPhase}
          onRetry={() => { refreshModel(); refreshOverview(); }}
        />
        <HeroCard />
        <PersonalInsightsSection 
          sectionRef={insightsRef} 
          onOpen={setModalItem} 
          aiInsights={aiInsights}
          loading={loading}
          modelStatus={modelStatus}
        />
        <HealthTipsSection sectionRef={tipsRef} onOpen={setModalItem} />
        <HRVCard />
        <QuickActions />
      </div>
      <Footer />

      <InsightDetailModal
        open={modalItem !== null}
        item={modalItem}
        onClose={() => setModalItem(null)}
      />
    </div>
  );
}

export default Insights;